/**
 * Module #34 Phase A — copilot chat state hook.
 *
 * Supports the non-streaming send() (JSON round-trip) and the streaming
 * sendStreaming() (SSE token-by-token) paths against the IE Worker, plus the
 * chunk-5d confirmation flow: a Tier-2/3 action pauses the turn with a
 * `pending_action`, the UI renders a confirmation card, and confirmAction()
 * resumes the turn against /v1/copilot/chat/confirm (streaming the post-fire
 * acknowledgement). The chat UI prefers sendStreaming; send() is a fallback.
 */

import { useCallback, useMemo, useState } from 'react';
import { useSession } from './useSession';
import { fetchWithAuth } from '../lib/accessToken';

/** A downloadable report (CSV) the copilot produced — rendered as a download
 *  button on the assistant turn. The CSV is base64 (UTF-8); the chat decodes
 *  it to a Blob on click. Ephemeral: not re-hydrated on conversation reload. */
export interface ReportAttachment {
  kind: 'report_csv';
  filename: string;
  csv_base64: string;
  report_key?: string;
  row_count?: number;
}

export interface CopilotMessage {
  role: 'user' | 'assistant';
  text: string;
  /** Server-assigned position for assistant turns. */
  position?: number;
  /** Anthropic model the assistant turn was generated with. */
  model?: string;
  /** Downloadable report artifacts attached to this assistant turn. */
  attachments?: ReportAttachment[];
  /** When the message landed in component state. */
  createdAt: string;
}

/** Descriptor for an 'input'-tier value card (onboarding C2). Mirrors the
 * server InputDescriptor (intelligence tools/types.ts). */
export interface InputDescriptor {
  kind: 'branding' | 'domain' | 'view_selector';
  arg_names: string[];
  initial?: Record<string, unknown>;
  context?: Record<string, unknown>;
}

/** A held action awaiting the user before it fires (chunk 5d + onboarding C2). */
export interface PendingAction {
  tool_use_id: string;
  tool_name: string;
  tier: 'tier2' | 'tier3' | 'input';
  /** Short human-readable action label, e.g. "Void invoice DTA-2026-00145". */
  title: string;
  description: string;
  /** tier3 only — the exact phrase the user must type to confirm. */
  confirmation_phrase?: string;
  /** 'input' tier only — which value card to render + which args it sets. */
  input_descriptor?: InputDescriptor;
}

/** One row of the confirmAction payload — the user's decision per action. */
export interface ConfirmDecision {
  tool_use_id: string;
  confirmed: boolean;
  confirmation_text?: string;
  /** 'input' tier only — the owner-supplied value merged into the tool args. */
  value?: Record<string, unknown>;
}

/** Set of actions awaiting confirmation in the current conversation. */
export interface PendingState {
  conversationId: string;
  actions: PendingAction[];
}

export interface CopilotUsageState {
  /** Turns the caller has consumed today. */
  turnsUsedToday: number;
  /** Tier limit (or -1 = unmetered). */
  turnsLimitToday: number;
  platformCostTodayTenthsCents: number;
  platformCostCapTenthsCents: number;
}

export type CopilotStatus =
  | { kind: 'idle' }
  | { kind: 'sending' }
  /** Stream open; tokens arriving. UI can show a live cursor. */
  | { kind: 'streaming' }
  /** Turn paused on a Tier-2/3 action; waiting for the user to confirm/cancel. */
  | { kind: 'awaiting_confirmation' }
  | { kind: 'error'; status: number; code: string; detail: string };

export interface UseCopilotReturn {
  messages: CopilotMessage[];
  status: CopilotStatus;
  usage: CopilotUsageState | null;
  conversationId: string | null;
  configured: boolean;
  /** Actions awaiting confirmation, or null. */
  pending: PendingState | null;
  /** Non-streaming send (kept as fallback). */
  send: (text: string) => Promise<void>;
  /**
   * Streaming send — POSTs to /v1/copilot/chat/stream and appends the
   * assistant reply token-by-token. May resolve into a `pending` state if the
   * copilot wants to fire a gated action. Preferred path for the chat UI.
   */
  sendStreaming: (text: string) => Promise<void>;
  /**
   * Resume a paused turn: fire (or decline) the held actions per the user's
   * decisions, then stream the copilot's acknowledgement. Clears `pending`.
   */
  confirmAction: (decisions: ConfirmDecision[]) => Promise<void>;
  reset: () => void;
  /**
   * Load an existing conversation's messages into the hook state and pin
   * subsequent sends to that conversation_id. Returns false on failure.
   */
  loadConversation: (id: string) => Promise<boolean>;
}

interface ConversationDetailApiResponse {
  conversation_id: string;
  title: string | null;
  summary: string | null;
  created_at: string;
  messages: Array<{
    position: number;
    role: 'user' | 'assistant' | 'tool' | 'system';
    content: Record<string, unknown>;
    tool_name: string | null;
    tool_call_id: string | null;
    model: string | null;
    tokens_input: number | null;
    tokens_output: number | null;
    created_at: string;
  }>;
}

interface ChatApiResponse {
  conversation_id: string;
  reply: string;
  position: number;
  model: string;
  tokens_input: number;
  tokens_output: number;
  /** Present on a paused (Tier-2/3) turn. */
  status?: string;
  pending_actions?: PendingAction[];
  /** Downloadable report artifacts (CSV) produced this turn. */
  artifacts?: ReportAttachment[];
  usage: {
    turns_used_today: number;
    turns_limit_today: number;
    platform_cost_tenths_cents_today: number;
    platform_cost_cap_tenths_cents: number;
  };
}

interface ChatApiError {
  error: string;
  detail: string;
}

function ieBaseUrl(): string | null {
  const url = import.meta.env.VITE_INTELLIGENCE_BASE_URL as string | undefined;
  return url && url.length > 0 ? url.replace(/\/$/, '') : null;
}

function usageFrom(u: Record<string, number> | undefined): CopilotUsageState | null {
  if (u === undefined) return null;
  return {
    turnsUsedToday: u['turns_used_today'] ?? 0,
    turnsLimitToday: u['turns_limit_today'] ?? -1,
    platformCostTodayTenthsCents: u['platform_cost_tenths_cents_today'] ?? 0,
    platformCostCapTenthsCents: u['platform_cost_cap_tenths_cents'] ?? 0,
  };
}

export function useCopilot(): UseCopilotReturn {
  const { session, userId } = useSession();
  const base = useMemo(() => ieBaseUrl(), []);
  const [messages, setMessages] = useState<CopilotMessage[]>([]);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [status, setStatus] = useState<CopilotStatus>({ kind: 'idle' });
  const [usage, setUsage] = useState<CopilotUsageState | null>(null);
  const [pending, setPending] = useState<PendingState | null>(null);

  const configured = base !== null && session?.access_token !== undefined;

  /**
   * Consume an SSE stream from a chat/confirm response into the assistant
   * slot at `assistantIdx`. Handles token / done / pending_action / error.
   * Returns when the stream closes.
   */
  const consumeStream = useCallback(async (res: Response, assistantIdx: number): Promise<void> => {
    if (res.body === null) {
      setStatus({
        kind: 'error',
        status: res.status,
        code: 'no_stream_body',
        detail: 'The server returned no stream.',
      });
      return;
    }
    setStatus({ kind: 'streaming' });

    const appendDelta = (delta: string): void => {
      setMessages((prev) => {
        if (assistantIdx < 0 || assistantIdx >= prev.length) return prev;
        const copy = prev.slice();
        const cur = copy[assistantIdx];
        if (cur === undefined) return prev;
        copy[assistantIdx] = { ...cur, text: cur.text + delta };
        return copy;
      });
    };

    const attachArtifact = (att: ReportAttachment): void => {
      setMessages((prev) => {
        if (assistantIdx < 0 || assistantIdx >= prev.length) return prev;
        const copy = prev.slice();
        const cur = copy[assistantIdx];
        if (cur === undefined) return prev;
        copy[assistantIdx] = { ...cur, attachments: [...(cur.attachments ?? []), att] };
        return copy;
      });
    };

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let gotText = false;
    let gotArtifact = false;
    let terminal = false; // a done / pending_action / error frame closed the turn
    try {
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let sep: number;
        while ((sep = buffer.indexOf('\n\n')) !== -1) {
          const frame = buffer.slice(0, sep);
          buffer = buffer.slice(sep + 2);
          const lines = frame.split('\n');
          const eventLine = lines.find((l) => l.startsWith('event:'));
          const dataLine = lines.find((l) => l.startsWith('data:'));
          if (eventLine === undefined || dataLine === undefined) continue;
          const event = eventLine.slice(6).trim();
          const dataStr = dataLine.slice(5).trim();
          let data: Record<string, unknown>;
          try {
            data = JSON.parse(dataStr) as Record<string, unknown>;
          } catch {
            continue;
          }
          if (event === 'token') {
            if (typeof data['text'] === 'string') {
              gotText = true;
              appendDelta(data['text']);
            }
          } else if (event === 'artifact') {
            const filename = typeof data['filename'] === 'string' ? data['filename'] : null;
            const csvB64 = typeof data['csv_base64'] === 'string' ? data['csv_base64'] : null;
            if (filename !== null && csvB64 !== null) {
              gotArtifact = true;
              attachArtifact({
                kind: 'report_csv',
                filename,
                csv_base64: csvB64,
                ...(typeof data['report_key'] === 'string' && { report_key: data['report_key'] }),
                ...(typeof data['row_count'] === 'number' && { row_count: data['row_count'] }),
              });
            }
          } else if (event === 'done') {
            terminal = true;
            if (typeof data['conversation_id'] === 'string')
              setConversationId(data['conversation_id']);
            const u = usageFrom(data['usage'] as Record<string, number> | undefined);
            if (u !== null) setUsage(u);
            setMessages((prev) => {
              if (assistantIdx < 0 || assistantIdx >= prev.length) return prev;
              const copy = prev.slice();
              const cur = copy[assistantIdx];
              if (cur === undefined) return prev;
              const stamped: CopilotMessage = { ...cur };
              if (typeof data['model'] === 'string') stamped.model = data['model'];
              if (typeof data['position'] === 'number') stamped.position = data['position'];
              copy[assistantIdx] = stamped;
              return copy;
            });
            setPending(null);
            setStatus({ kind: 'idle' });
          } else if (event === 'pending_action') {
            terminal = true;
            const convId =
              typeof data['conversation_id'] === 'string' ? data['conversation_id'] : null;
            if (convId !== null) setConversationId(convId);
            const u = usageFrom(data['usage'] as Record<string, number> | undefined);
            if (u !== null) setUsage(u);
            const actions = Array.isArray(data['actions'])
              ? (data['actions'] as PendingAction[])
              : [];
            // Drop the assistant slot if the copilot emitted no lead-in text
            // AND no download — an empty bubble above the card looks broken.
            if (!gotText && !gotArtifact) {
              setMessages((prev) => {
                if (assistantIdx < 0 || assistantIdx >= prev.length) return prev;
                const copy = prev.slice();
                copy.splice(assistantIdx, 1);
                return copy;
              });
            }
            if (convId !== null && actions.length > 0) {
              setPending({ conversationId: convId, actions });
              setStatus({ kind: 'awaiting_confirmation' });
            } else {
              setStatus({ kind: 'idle' });
            }
          } else if (event === 'error') {
            terminal = true;
            setStatus({
              kind: 'error',
              status: 502,
              code: typeof data['error'] === 'string' ? data['error'] : 'stream_error',
              detail:
                typeof data['detail'] === 'string'
                  ? data['detail']
                  : 'The stream failed mid-reply.',
            });
          }
        }
      }
    } catch (err) {
      setStatus({
        kind: 'error',
        status: 0,
        code: 'stream_read_error',
        detail: err instanceof Error ? err.message : 'Stream read error',
      });
      return;
    }
    // Stream closed without a terminal frame — fall back to idle so the UI
    // isn't stuck "streaming" forever.
    if (!terminal) setStatus({ kind: 'idle' });
  }, []);

  const send = useCallback(
    async (text: string): Promise<void> => {
      const trimmed = text.trim();
      if (trimmed.length === 0) return;
      if (base === null) {
        setStatus({
          kind: 'error',
          status: 0,
          code: 'not_configured',
          detail: 'VITE_INTELLIGENCE_BASE_URL is not configured.',
        });
        return;
      }
      if (userId === null) {
        setStatus({
          kind: 'error',
          status: 0,
          code: 'no_session',
          detail: 'You need to be signed in to use the copilot.',
        });
        return;
      }

      // A new message supersedes any unconfirmed action (the server auto-
      // declines it); clear the local card so the UI stays consistent.
      setPending(null);
      setMessages((prev) => [
        ...prev,
        { role: 'user', text: trimmed, createdAt: new Date().toISOString() },
      ]);
      setStatus({ kind: 'sending' });

      let res: Response;
      try {
        res = await fetchWithAuth(`${base}/v1/copilot/chat`, {
          method: 'POST',
          headers: {
            'content-type': 'application/json',
          },
          body: JSON.stringify({
            ...(conversationId !== null && { conversation_id: conversationId }),
            message: trimmed,
          }),
        });
      } catch (err) {
        setStatus({
          kind: 'error',
          status: 0,
          code: 'network_error',
          detail: err instanceof Error ? err.message : 'Network error',
        });
        return;
      }

      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as ChatApiError | null;
        setStatus({
          kind: 'error',
          status: res.status,
          code: body?.error ?? `http_${res.status}`,
          detail: body?.detail ?? `Server returned HTTP ${res.status}`,
        });
        return;
      }

      const data = (await res.json()) as ChatApiResponse;
      setConversationId(data.conversation_id);
      setUsage(usageFrom(data.usage as unknown as Record<string, number>));
      const replyAttachments =
        data.artifacts !== undefined && data.artifacts.length > 0 ? data.artifacts : undefined;
      if (data.reply.length > 0 || replyAttachments !== undefined) {
        setMessages((prev) => [
          ...prev,
          {
            role: 'assistant',
            text: data.reply,
            position: data.position,
            model: data.model,
            createdAt: new Date().toISOString(),
            ...(replyAttachments !== undefined && { attachments: replyAttachments }),
          },
        ]);
      }
      if (data.status === 'awaiting_confirmation' && (data.pending_actions?.length ?? 0) > 0) {
        setPending({ conversationId: data.conversation_id, actions: data.pending_actions ?? [] });
        setStatus({ kind: 'awaiting_confirmation' });
      } else {
        setStatus({ kind: 'idle' });
      }
    },
    [base, userId, conversationId],
  );

  const sendStreaming = useCallback(
    async (text: string): Promise<void> => {
      const trimmed = text.trim();
      if (trimmed.length === 0) return;
      if (base === null) {
        setStatus({
          kind: 'error',
          status: 0,
          code: 'not_configured',
          detail: 'VITE_INTELLIGENCE_BASE_URL is not configured.',
        });
        return;
      }
      if (userId === null) {
        setStatus({
          kind: 'error',
          status: 0,
          code: 'no_session',
          detail: 'You need to be signed in to use the copilot.',
        });
        return;
      }

      // Optimistic user turn + empty assistant slot the stream fills in.
      setPending(null);
      let assistantIdx = -1;
      setMessages((prev) => {
        const next: CopilotMessage[] = [
          ...prev,
          { role: 'user', text: trimmed, createdAt: new Date().toISOString() },
          { role: 'assistant', text: '', createdAt: new Date().toISOString() },
        ];
        assistantIdx = next.length - 1;
        return next;
      });
      setStatus({ kind: 'sending' });

      let res: Response;
      try {
        res = await fetchWithAuth(`${base}/v1/copilot/chat/stream`, {
          method: 'POST',
          headers: {
            'content-type': 'application/json',
          },
          body: JSON.stringify({
            ...(conversationId !== null && { conversation_id: conversationId }),
            message: trimmed,
          }),
        });
      } catch (err) {
        setStatus({
          kind: 'error',
          status: 0,
          code: 'network_error',
          detail: err instanceof Error ? err.message : 'Network error',
        });
        return;
      }

      // Pre-stream gate failures (400/403/429/503) come back as JSON, not SSE.
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as ChatApiError | null;
        setStatus({
          kind: 'error',
          status: res.status,
          code: body?.error ?? `http_${res.status}`,
          detail: body?.detail ?? `Server returned HTTP ${res.status}`,
        });
        return;
      }

      await consumeStream(res, assistantIdx);
    },
    [base, userId, conversationId, consumeStream],
  );

  const confirmAction = useCallback(
    async (decisions: ConfirmDecision[]): Promise<void> => {
      if (base === null || userId === null) return;
      const convId = pending?.conversationId ?? conversationId;
      if (convId === null) return;

      // Clear the card immediately; the resume streams the acknowledgement
      // into a fresh assistant slot.
      setPending(null);
      let assistantIdx = -1;
      setMessages((prev) => {
        const next: CopilotMessage[] = [
          ...prev,
          { role: 'assistant', text: '', createdAt: new Date().toISOString() },
        ];
        assistantIdx = next.length - 1;
        return next;
      });
      setStatus({ kind: 'sending' });

      let res: Response;
      try {
        res = await fetchWithAuth(`${base}/v1/copilot/chat/confirm`, {
          method: 'POST',
          headers: {
            'content-type': 'application/json',
          },
          body: JSON.stringify({ conversation_id: convId, decisions }),
        });
      } catch (err) {
        setStatus({
          kind: 'error',
          status: 0,
          code: 'network_error',
          detail: err instanceof Error ? err.message : 'Network error',
        });
        return;
      }

      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as ChatApiError | null;
        // Drop the empty assistant slot we optimistically added.
        setMessages((prev) => {
          if (assistantIdx < 0 || assistantIdx >= prev.length) return prev;
          const cur = prev[assistantIdx];
          if (cur === undefined || cur.text.length > 0) return prev;
          const copy = prev.slice();
          copy.splice(assistantIdx, 1);
          return copy;
        });
        setStatus({
          kind: 'error',
          status: res.status,
          code: body?.error ?? `http_${res.status}`,
          detail: body?.detail ?? `Server returned HTTP ${res.status}`,
        });
        return;
      }

      await consumeStream(res, assistantIdx);
    },
    [base, userId, conversationId, pending, consumeStream],
  );

  const reset = useCallback((): void => {
    setMessages([]);
    setConversationId(null);
    setPending(null);
    setStatus({ kind: 'idle' });
  }, []);

  const loadConversation = useCallback(
    async (id: string): Promise<boolean> => {
      if (base === null || userId === null) return false;
      setPending(null);
      setStatus({ kind: 'sending' });
      let res: Response;
      try {
        res = await fetchWithAuth(`${base}/v1/copilot/conversations/${encodeURIComponent(id)}`);
      } catch (err) {
        setStatus({
          kind: 'error',
          status: 0,
          code: 'network_error',
          detail: err instanceof Error ? err.message : 'Network error',
        });
        return false;
      }
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as ChatApiError | null;
        setStatus({
          kind: 'error',
          status: res.status,
          code: body?.error ?? `http_${res.status}`,
          detail: body?.detail ?? `Server returned HTTP ${res.status}`,
        });
        return false;
      }
      const data = (await res.json()) as ConversationDetailApiResponse;
      const loaded: CopilotMessage[] = [];
      for (const m of data.messages) {
        if (m.role !== 'user' && m.role !== 'assistant') continue; // hide tool / system turns
        const content = m.content as { text?: unknown; content?: unknown };
        let text = '';
        if (typeof content.text === 'string') {
          text = content.text;
        } else if (Array.isArray(content.content)) {
          // Stringify only the text blocks so the chat surface stays text-only.
          for (const block of content.content) {
            if (
              typeof block === 'object' &&
              block !== null &&
              (block as { type?: string }).type === 'text' &&
              typeof (block as { text?: unknown }).text === 'string'
            ) {
              text += (text.length > 0 ? '\n' : '') + (block as { text: string }).text;
            }
          }
        }
        if (text.length === 0) continue;
        const message: CopilotMessage = {
          role: m.role,
          text,
          createdAt: m.created_at,
        };
        if (m.model !== null) message.model = m.model;
        if (m.position !== undefined) message.position = m.position;
        loaded.push(message);
      }
      setMessages(loaded);
      setConversationId(data.conversation_id);
      setStatus({ kind: 'idle' });
      return true;
    },
    [base, userId],
  );

  return {
    messages,
    status,
    usage,
    conversationId,
    configured,
    pending,
    send,
    sendStreaming,
    confirmAction,
    reset,
    loadConversation,
  };
}
